import type {
  OverrideActivityKey,
  TicketOverrideRoute,
  TicketOverrideSlot,
  TicketOverridesView,
  UiCatalogueModel,
  UiVendorSwitch,
} from '@nightshift/ui-types';

import type { RouteRow } from './route-list.ts';

export const EXECUTION_STATE_OPTIONS = ['Refining', 'Implementing', 'AI Review'] as const;
export type ExecutionState = (typeof EXECUTION_STATE_OPTIONS)[number];

const EXECUTION_STEP_OPTIONS = {
  Refining: ['Standard', 'Research', 'Planning'],
  Implementing: ['Merge or Rebase'],
  'AI Review': [],
} as const satisfies Record<ExecutionState, readonly string[]>;

/** Keep in lockstep with `REASONING_EFFORTS` in `core/src/model-catalog.ts`. */
const REASONING_EFFORT_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'xhigh', label: 'Extra High' },
  { id: 'max', label: 'Max' },
  { id: 'ultra', label: 'Ultra' },
] as const;

const REASONING_EFFORT_IDS = new Set<string>(REASONING_EFFORT_OPTIONS.map((entry) => entry.id));

export interface CatalogModel {
  id: string;
  label: string;
  harness: string;
  supportsReasoning?: boolean;
  /**
   * Thinking levels this model accepts, from the live catalogue. Absent on the offline
   * fallback, where a reasoning-capable model offers every known level.
   */
  efforts?: readonly string[];
  /** The level the source marks as default; falls back to `DEFAULT_REASONING_EFFORT`. */
  defaultEffort?: string | null;
}

/**
 * Offline copy of `AGENT_MODELS`. The Overrides tab prefers the Settings catalogue
 * so kill-switched models disappear; this is the fallback when that fetch fails.
 * Thinking is not listed — it is chosen next to the model on each override row.
 */
const FALLBACK_MODELS: readonly CatalogModel[] = [
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', harness: 'claude', supportsReasoning: true },
  { id: 'claude-opus-5', label: 'Claude Opus 5', harness: 'claude', supportsReasoning: true },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', harness: 'claude', supportsReasoning: true },
  { id: 'gpt-6-astra', label: 'GPT-6 Astra', harness: 'codex', supportsReasoning: true },
  { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', harness: 'codex', supportsReasoning: true },
  { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', harness: 'codex', supportsReasoning: true },
  { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', harness: 'codex', supportsReasoning: true },
  { id: 'cursor:gpt-6-astra', label: 'GPT-6 Astra', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:gpt-5.6-sol', label: 'GPT-5.6 Sol', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:gpt-5.6-terra', label: 'GPT-5.6 Terra', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:gpt-5.6-luna', label: 'GPT-5.6 Luna', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor-grok-4.6', label: 'Cursor Grok 4.6', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:claude-fable-5-1', label: 'Claude Fable 5.1', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:claude-opus-5', label: 'Claude Opus 5', harness: 'cursor', supportsReasoning: true },
  { id: 'cursor:claude-sonnet-5', label: 'Claude Sonnet 5', harness: 'cursor', supportsReasoning: true },
  { id: 'composer-2.5', label: 'Composer 2.5', harness: 'cursor' },
  { id: 'composer-2.5-fast', label: 'Composer 2.5 Fast', harness: 'cursor' },
  { id: 'kimi-code/k3-256k', label: 'Kimi K3 256K', harness: 'kimi' },
  { id: 'kimi-for-coding', label: 'Kimi K2.7 Coding', harness: 'kimi' },
];

function composeModelId(catalogId: string, effort: string | null): string {
  return effort ? `${catalogId}/${effort}` : catalogId;
}

function parseStoredModelId(modelId: string): { catalogId: string; reasoningEffort: string | null } {
  const slash = modelId.lastIndexOf('/');
  if (slash > 0) {
    const catalogId = modelId.slice(0, slash);
    const effort = modelId.slice(slash + 1);
    if (REASONING_EFFORT_IDS.has(effort)) {
      return { catalogId, reasoningEffort: effort };
    }
  }
  return { catalogId: modelId, reasoningEffort: null };
}

export interface OverrideEditorRow {
  id: string;
  state: ExecutionState;
  step: string | null;
  slot: TicketOverrideSlot;
}


export function cloneSlot(slot?: TicketOverrideSlot): TicketOverrideSlot {
  return {
    machine: slot?.machine ?? null,
    harness: slot?.harness ?? null,
    models: slot?.models ? [...slot.models] : null,
    ...(slot?.routes && slot.routes.length > 0 ? { routes: slot.routes.map((route) => ({ ...route })) } : {}),
  };
}

/**
 * The four-column rows a slot shows. Canonical `routes` win; a slot saved before rows existed
 * is read from its `harness` + `models`, inferring the harness per model from the catalogue
 * when the slot left it on Auto, so nothing already pinned disappears from the editor.
 */
export function rowsFromSlot(slot: TicketOverrideSlot, catalog: ReadonlyArray<CatalogModel> = FALLBACK_MODELS): RouteRow[] {
  if (slot.routes && slot.routes.length > 0) {
    return slot.routes.map((route) => ({
      machine: route.machine ?? slot.machine ?? null,
      harness: route.harness,
      model: route.model,
      thinking: route.thinking ?? null,
    }));
  }
  return (slot.models ?? []).flatMap((id) => {
    const parsed = parseStoredModelId(id);
    const harness = slot.harness ?? catalog.find((entry) => entry.id === parsed.catalogId)?.harness;
    if (!harness) return [];
    return [{ machine: slot.machine ?? null, harness, model: parsed.catalogId, thinking: parsed.reasoningEffort }];
  });
}

function commonValue(values: readonly (string | null)[]): string | null {
  const first = values[0] ?? null;
  if (first === null) return null;
  return values.every((value) => value !== null && value.toLowerCase() === first.toLowerCase()) ? first : null;
}

/**
 * A slot from its rows. `routes` is what the resolver reads; `machine`, `harness` and `models`
 * are mirrored for the older readers (timeline, Auto labels, enablement checks): the machine
 * pin is the one machine every row names, the harness the one harness every row uses.
 * No rows is Auto.
 */
export function slotFromRows(rows: readonly RouteRow[]): TicketOverrideSlot {
  if (rows.length === 0) return cloneSlot();
  const routes: TicketOverrideRoute[] = rows.map((row) => ({
    machine: row.machine,
    harness: row.harness,
    model: row.model,
    thinking: row.thinking,
  }));
  return {
    machine: commonValue(rows.map((row) => row.machine)),
    harness: commonValue(rows.map((row) => row.harness)),
    models: rows.map((row) => composeModelId(row.model, row.thinking)),
    routes,
  };
}

/** True when the slot pins nothing: every field Auto and no rows. */
export function slotIsAuto(slot: TicketOverrideSlot): boolean {
  return slot.machine === null && slot.harness === null && slot.models === null && !(slot.routes && slot.routes.length > 0);
}

/**
 * The live catalogue narrowed by the vendor and model kill switches, in the shape the shared
 * route editor takes. Nothing is added: a family the switches do not know stays offered.
 */
export function catalogueWithSwitches(
  models: readonly UiCatalogueModel[],
  vendors: readonly UiVendorSwitch[],
): UiCatalogueModel[] {
  if (vendors.length === 0) return [...models];
  const enabledHarnesses = new Set(vendors.filter((vendor) => vendor.enabled).map((vendor) => vendor.vendor));
  const disabledModels = new Set(
    vendors.flatMap((vendor) => (vendor.models ?? []).filter((model) => !model.enabled).map((model) => model.id)),
  );
  return models.filter((model) => enabledHarnesses.has(model.harness) && !disabledModels.has(model.id));
}

export function scopeKey(state: ExecutionState, step: string | null): string {
  return step ? `${state}/${step}` : state;
}

export function stepOptions(state: ExecutionState): readonly string[] {
  return EXECUTION_STEP_OPTIONS[state];
}

export function rowsFromOverrides(overrides: TicketOverridesView): OverrideEditorRow[] {
  const rows: OverrideEditorRow[] = [];
  for (const state of EXECUTION_STATE_OPTIONS) {
    const slot = overrides.states[state];
    if (slot) {
      rows.push({
        id: `saved-${state}`,
        state,
        step: null,
        slot: cloneSlot(slot),
      });
    }
    for (const step of stepOptions(state)) {
      const key = `${state}/${step}` as OverrideActivityKey;
      const activitySlot = overrides.activities[key];
      if (activitySlot) {
        rows.push({
          id: `saved-${key}`,
          state,
          step,
          slot: cloneSlot(activitySlot),
        });
      }
    }
  }
  return rows;
}

export function duplicateScopes(rows: readonly OverrideEditorRow[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of rows) {
    const key = scopeKey(row.state, row.step);
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  }
  return duplicates;
}

export function firstUnusedScope(
  rows: readonly OverrideEditorRow[],
): { state: ExecutionState; step: string | null } | null {
  const used = new Set(rows.map((row) => scopeKey(row.state, row.step)));
  for (const state of EXECUTION_STATE_OPTIONS) {
    if (!used.has(state)) return { state, step: null };
    for (const step of stepOptions(state)) {
      if (!used.has(scopeKey(state, step))) return { state, step };
    }
  }
  return null;
}

export function overridesFromRows(
  ticket: TicketOverrideSlot,
  verificationProfile: string | null,
  rows: readonly OverrideEditorRow[],
  preserve?: TicketOverridesView,
): TicketOverridesView {
  const states: TicketOverridesView['states'] = { ...preserve?.states };
  const activities: TicketOverridesView['activities'] = { ...preserve?.activities };
  for (const state of EXECUTION_STATE_OPTIONS) {
    delete states[state];
    for (const step of stepOptions(state)) {
      delete activities[`${state}/${step}` as OverrideActivityKey];
    }
  }
  for (const row of rows) {
    if (row.step === null) {
      states[row.state] = cloneSlot(row.slot);
    } else {
      activities[`${row.state}/${row.step}` as OverrideActivityKey] = cloneSlot(row.slot);
    }
  }
  return {
    ticket: cloneSlot(ticket),
    states,
    activities,
    verificationProfile,
  };
}
